"use client";

import { useEffect, useState, useCallback, useReducer, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MessageCircle } from "lucide-react";
import { Button } from "ui";
import type { ChannelMessage, OptimisticMessage } from "@/lib/types/channel";
import type { MessagesAction } from "../../store/messages-reducer";
import { messagesReducer, initialState } from "../../store/messages-reducer";
import { makeOptimisticMessage } from "../../hooks/make-optimistic-message";
import MessageItem, { type MessageSurface } from "../message-list/message-item";
import MessageComposer from "../message-composer/message-composer";

const CONTINUATION_THRESHOLD_MS = 5 * 60 * 1000;

type CurrentMemberInfo = {
  id: string;
  role: string;
  displayName: string | null;
  image: string | null;
};

type Props = {
  parentMessage: OptimisticMessage | null;
  currentMember: CurrentMemberInfo;
  isAdmin: boolean;
  onClose: () => void;
  onParentDispatch: React.Dispatch<MessagesAction>;
  /** Fetch replies for a parent message from the relevant backend. */
  fetchReplies: (parentMessageId: string) => Promise<OptimisticMessage[]>;
  /** Persist a reply and resolve with the confirmed message. */
  persistReply: (
    content: string,
    parentMessageId: string,
  ) => Promise<ChannelMessage>;
  /** Identifier passed through to the composer (channel or conversation id). */
  composerId: string;
  /** Router that owns these messages — routes edit/delete/react correctly. */
  surface: MessageSurface;
  /** When true, the member cannot post — disables the reply composer. */
  replyDisabled?: boolean;
};

function isContinuationMessage(
  current: OptimisticMessage,
  prev: OptimisticMessage | null,
): boolean {
  if (!prev) return false;
  if (prev.member.id !== current.member.id) return false;
  const timeDiff =
    new Date(current.createdAt).getTime() - new Date(prev.createdAt).getTime();
  return timeDiff < CONTINUATION_THRESHOLD_MS;
}

export default function ThreadPanel({
  parentMessage,
  currentMember,
  isAdmin,
  onClose,
  onParentDispatch,
  fetchReplies,
  persistReply,
  composerId,
  surface,
  replyDisabled = false,
}: Props) {
  const [state, dispatch] = useReducer(messagesReducer, initialState);
  const [isLoading, setIsLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Callers derive these from the tRPC utils client, which hands back a new
  // proxy on every property access — so they are new on every render. Held in
  // refs to keep them out of effect deps; depending on their identity would
  // re-run the reply fetch on every render.
  const fetchRepliesRef = useRef(fetchReplies);
  fetchRepliesRef.current = fetchReplies;
  const persistReplyRef = useRef(persistReply);
  persistReplyRef.current = persistReply;

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, []);

  const loadReplies = useCallback(async (messageId: string) => {
    setIsLoading(true);
    try {
      const replies = await fetchRepliesRef.current(messageId);
      dispatch({ type: "INIT", messages: replies });
    } catch {
      // silently fail — empty state shown
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (parentMessage?.id) {
      dispatch({ type: "INIT", messages: [] });
      loadReplies(parentMessage.id);
    }
  }, [parentMessage?.id, loadReplies]);

  // Soft-deleted replies stay in state.messages (rendered as null) but must not
  // count — mirror the backend, which excludes deletedAt rows from the count.
  const visibleReplyCount = state.messages.reduce(
    (count, m) => (m.deletedAt ? count : count + 1),
    0,
  );

  // Update reply count on parent message when replies change
  useEffect(() => {
    if (!parentMessage) return;
    onParentDispatch({
      type: "PATCH",
      messageId: parentMessage.id,
      patch: { _count: { replies: visibleReplyCount } },
    });
  }, [visibleReplyCount, parentMessage?.id, onParentDispatch]);

  // Auto-scroll to bottom when new replies arrive
  useEffect(() => {
    if (state.messages.length > 0) {
      scrollToBottom();
    }
  }, [state.messages.length, scrollToBottom]);

  // Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSendReply = useCallback(
    async (content: string) => {
      if (!parentMessage) return;
      const optimistic = makeOptimisticMessage(
        content,
        parentMessage.id,
        currentMember,
      );
      const tempId = optimistic.id;
      dispatch({ type: "APPEND_OPTIMISTIC", message: optimistic });
      try {
        const confirmed = await persistReplyRef.current(
          content,
          parentMessage.id,
        );
        dispatch({
          type: "CONFIRM_OPTIMISTIC",
          tempId,
          confirmed: { ...confirmed, optimisticStatus: "sent" },
        });
      } catch {
        dispatch({ type: "FAIL_OPTIMISTIC", tempId });
      }
    },
    [parentMessage, currentMember],
  );

  const parentDisplayName =
    parentMessage?.member.profile?.displayName ?? "Unknown Member";

  const replyCount = visibleReplyCount;
  const hasReplies = replyCount > 0;

  return (
    <AnimatePresence>
      {parentMessage && (
        <motion.div
          key="thread-panel"
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", stiffness: 350, damping: 35 }}
          className="flex h-full w-[360px] flex-shrink-0 flex-col border-l border-border bg-background"
        >
          {/* Header */}
          <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-4 py-3">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-foreground">Thread</h3>
              <p className="truncate text-xs text-muted-foreground">
                {parentDisplayName}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              icon={<X />}
              className="ml-2 flex-shrink-0"
              aria-label="Close thread"
            />
          </div>

          {/* Original message — capped so a long parent can't push the reply
              list and composer out of the panel; it scrolls on its own. The
              top padding leaves room for the hover toolbar, which sits half
              above the message and would otherwise be clipped by the
              overflow container. */}
          <div className="max-h-[45%] flex-shrink-0 overflow-y-auto border-b border-border pt-4">
            <MessageItem
              message={parentMessage}
              currentMemberId={currentMember.id}
              isAdmin={isAdmin}
              dispatch={onParentDispatch}
              onReplyClick={() => {}}
              onRetry={() => {}}
              surface={surface}
              replyDisabled
            />
          </div>

          {/* Reply count divider */}
          <div className="flex flex-shrink-0 items-center gap-3 px-4 py-2">
            <hr className="flex-1 border-border" />
            <span className="flex-shrink-0 text-xs font-medium text-muted-foreground">
              {replyCount} {replyCount === 1 ? "reply" : "replies"}
            </span>
            <hr className="flex-1 border-border" />
          </div>

          {/* Reply list */}
          <div
            ref={scrollContainerRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto py-2"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
              </div>
            ) : hasReplies ? (
              state.messages.map((reply, index) => {
                const prev = index > 0 ? state.messages[index - 1] : null;
                const isContinuation = isContinuationMessage(reply, prev);

                return (
                  <MessageItem
                    key={reply.id}
                    message={reply}
                    currentMemberId={currentMember.id}
                    isAdmin={isAdmin}
                    dispatch={dispatch}
                    onReplyClick={() => {}}
                    onRetry={() => {}}
                    isContinuation={isContinuation}
                    surface={surface}
                  />
                );
              })
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
                <MessageCircle className="mb-3 h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium text-muted-foreground">
                  No replies yet
                </p>
                <p className="mt-1 text-xs text-muted-foreground/60">
                  Start the conversation!
                </p>
              </div>
            )}
          </div>

          {/* Reply composer */}
          <div className="flex-shrink-0">
            <MessageComposer
              channelId={composerId}
              parentMessageId={parentMessage.id}
              placeholder="Reply in thread…"
              onSend={handleSendReply}
              compact
              disabled={replyDisabled}
              disabledMessage="You don't have permission to reply here."
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
